import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, Plus, Search, Edit, Archive, ArchiveRestore, Trash2, Filter, X, RefreshCw, Bookmark } from 'lucide-react';
import Header from './Header';
import SearchCounter from './SearchCounter';
import BrandBadge from './BrandBadge';
import BlacklistAlert from './BlacklistAlert';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  getAllOrders, 
  searchOrdersLocal, 
  deleteOrder,
  archiveOrder,
  unarchiveOrder,
  formatDate,
  getOrderTypeDisplayText,
  getStatusColor,
  confirmArchiveOrder
} from '../lib/orderService';
import { getBulkClientBlacklistStatusFromCache } from '../lib/blacklistService';

const OrdersList = () => {
  const { currentUser, userRole, showCacheRefreshButtons } = useAuth();
  const [orders, setOrders] = useState([]);
  const [localOrders, setLocalOrders] = useState([]);
  const [brands, setBrands] = useState([]);
  const [clientBlacklistStatus, setClientBlacklistStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(true);
  const [showBookmarked, setShowBookmarked] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  const [isLocalSearch, setIsLocalSearch] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isInitialMount = useRef(true);
  const itemsPerPage = 10;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchOrders();
    fetchBrands();
  }, [showArchived]);

  // Restore search state from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Only run if there are actually URL parameters to restore
    if (urlParams.toString()) {
      const search = urlParams.get('search');
      const type = urlParams.get('type');
      const bookmarked = urlParams.get('bookmarked');
      const hideArchived = urlParams.get('hideArchived');
      const page = urlParams.get('page');
      
      if (search) {
        setSearchTerm(search);
      }
      if (type && type !== 'all') {
        setTypeFilter(type);
      }
      if (bookmarked === 'true') {
        setShowBookmarked(true);
      }
      if (hideArchived === 'true') {
        setShowArchived(false);
      }
      if (page) {
        setCurrentPage(parseInt(page) || 1);
      }
      
      // Clear URL parameters after restoring state to keep URL clean
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Mark initial mount as complete after debounce delay to ensure all state updates are processed
    setTimeout(() => {
      isInitialMount.current = false;
    }, 500); // Longer than the 300ms debounce delay
  }, []); // Only run once on mount

  // Helper function to build URLs with current search state
  const buildUrlWithState = (basePath) => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (showBookmarked) params.set('bookmarked', 'true');
    if (!showArchived) params.set('hideArchived', 'true');
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    const queryString = params.toString();
    return `${basePath}${queryString ? `?${queryString}` : ''}`;
  };

  // Removed aggressive window focus refresh to prevent reloading on tab switch

  const fetchOrders = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError('');
      
      // Use getAllOrders which handles local-first with IndexedDB sync
      const ordersList = await getAllOrders(forceRefresh);
      
      // Sort by folio (descending - highest to lowest)
      const sortedOrders = ordersList.sort((a, b) => {
        const folioA = parseInt(a.folio) || 0;
        const folioB = parseInt(b.folio) || 0;
        return folioB - folioA; // Descending order
      });
      
      setOrders(sortedOrders);
      
      // Load blacklist status from cached data (no Firestore queries)
      await loadClientBlacklistStatusFromCache(sortedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      
      // Handle case where collection doesn't exist yet
      console.log('Orders collection may not exist yet, starting with empty list');
      setOrders([]);
      setError(''); // Don't show error for empty collection since this is expected behavior
    } finally {
      setLoading(false);
    }
  };

  const loadClientBlacklistStatusFromCache = async (ordersList) => {
    try {
      // Extract unique client IDs from orders
      const clientIds = [...new Set(
        ordersList
          .map(order => order.cliente)
          .filter(Boolean) // Remove null/undefined values
      )];

      if (clientIds.length === 0) {
        return;
      }

      // Get blacklist status from IndexedDB cache (no Firestore queries)
      const blacklistStatus = await getBulkClientBlacklistStatusFromCache(clientIds);
      setClientBlacklistStatus(prevStatus => ({
        ...prevStatus,
        ...blacklistStatus
      }));
      
    } catch (error) {
      console.error('Error loading cached client blacklist status for orders:', error);
      // Continue without blacklist status if it fails
    }
  };

  const fetchBrands = async () => {
    try {
      const brandsQuery = query(collection(db, 'marcas'));
      const brandsSnapshot = await getDocs(brandsQuery);
      const brandsList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBrands(brandsList);
    } catch (error) {
      console.log('Brands collection may not exist yet, starting with empty list');
      setBrands([]);
    }
  };

  // Helper function to get brand object by ID or name
  const getBrandByNameOrId = (identifier) => {
    if (!identifier || brands.length === 0) {
      return { nombre: identifier || 'Sin marca', color: '#6B7280' };
    }
    
    // Try to find by ID first (new format)
    let brand = brands.find(b => b.id === identifier);
    
    // If not found, try by name (backward compatibility)
    if (!brand) {
      brand = brands.find(b => b.nombre === identifier);
    }
    
    // Return found brand or fallback
    return brand || { nombre: identifier, color: '#6B7280' };
  };

  const performLocalSearch = useCallback(async () => {
    try {
      setIsLocalSearch(true);
      const results = await searchOrdersLocal(debouncedSearchTerm);
      setLocalOrders(results);
      
      // Load blacklist status for clients in search results
      await loadClientBlacklistStatusFromCache(results);
    } catch (error) {
      console.error('Error performing local search:', error);
      setLocalOrders([]);
    }
  }, [debouncedSearchTerm]);

  // Local search effect
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      performLocalSearch();
    } else {
      setLocalOrders([]);
      setIsLocalSearch(false);
    }
  }, [debouncedSearchTerm, performLocalSearch]);

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const handleForceRefresh = async () => {
    try {
      setIsRefreshing(true);
      // Force refresh both orders and brands from Firestore
      await Promise.all([
        fetchOrders(true), // Force refresh from Firestore
        fetchBrands() // Also refresh brands to get updated names
      ]);
    } catch (error) {
      console.error('Error refreshing cache:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getOrderTypeColor = (tipo) => {
    const typeColors = {
      'Reparación': 'bg-blue-50 text-blue-700',
      'Ensamble': 'bg-purple-50 text-purple-700',
      'Garantía': 'bg-orange-50 text-orange-700',
      'Fabricación': 'bg-gray-50 text-gray-700'
    };
    
    return typeColors[tipo] || 'bg-gray-50 text-gray-700';
  };

  const getPaymentStatusColor = (status) => {
    const statusColors = {
      'Sin cargo': 'bg-gray-50 text-gray-700',
      'Esperando pago': 'bg-red-50 text-red-700',
      'Pagado': 'bg-green-50 text-green-700'
    };
    
    return statusColors[status] || 'bg-gray-50 text-gray-700';
  };

  const filteredOrders = useMemo(() => {
    let baseOrders = isLocalSearch ? localOrders : orders;
    
    // Filter by type
    if (typeFilter !== 'all') {
      baseOrders = baseOrders.filter(order => order.tipo_de_orden === typeFilter);
    }
    
    // Filter by bookmark status
    if (showBookmarked) {
      baseOrders = baseOrders.filter(order => order.isBookmarked === true);
    }
    
    // Filter by archived status
    baseOrders = baseOrders.filter(order => {
      if (showArchived) {
        return true; // Show all when archived toggle is on
      } else {
        return !order.isArchived; // Show only non-archived by default
      }
    });
    
    return baseOrders;
  }, [isLocalSearch, localOrders, orders, typeFilter, showBookmarked, showArchived]);

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change (but not during initial URL restoration)
  useEffect(() => {
    if (!isInitialMount.current) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, typeFilter, showBookmarked, showArchived]);

  const handleArchive = async (order) => {
    try {
      // Use shared validation and confirmation function
      const confirmed = await confirmArchiveOrder(order);
      if (!confirmed) {
        return;
      }

      await archiveOrder(order.id);
      setSuccess(`Orden ${order.folio} ha sido archivada`);
      fetchOrders(true); // Force refresh to get updated data immediately
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error archiving order:', error);
      setError('Error al archivar la orden');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleUnarchive = async (order) => {
    try {
      if (!window.confirm(
        `¿Está seguro de desarchivar la orden ${order.folio}?\n\n` +
        `La orden volverá a la sección activa.`
      )) {
        return;
      }

      await unarchiveOrder(order.id);
      setSuccess(`Orden ${order.folio} ha sido desarchivada`);
      fetchOrders(true); // Force refresh to get updated data immediately
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error unarchiving order:', error);
      setError('Error al desarchivar la orden');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async (order) => {
    try {
      // Confirm deletion
      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente la orden ${order.folio}?\n\n` +
        `Esta acción no se puede deshacer. Si desea mantener el historial, ` +
        `considere archivar la orden en su lugar.`
      )) {
        return;
      }

      await deleteOrder(order.id);
      setSuccess(`Orden ${order.folio} ha sido eliminada permanentemente`);
      fetchOrders();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting order:', error);
      setError('Error al eliminar la orden');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getClientDisplayName = (order) => {
    if (order.clienteName) {
      return order.clienteName;
    }
    if (order.cliente) {
      return `Cliente: ${order.cliente}`;
    }
    return 'Sin cliente';
  };

  const renderClientInfo = (order, isArchived = false) => {
    // Get client blacklist status
    const clientStatus = clientBlacklistStatus[order.cliente] || {};
    const clientForAlert = {
      isBlacklisted: clientStatus.isBlacklisted || false,
      activeIncidentsCount: clientStatus.activeIncidentsCount || 0,
      mostRecentIncidentMotivo: clientStatus.mostRecentIncidentMotivo || null
    };

    // For company clients, display company name bold, contact name, and phone on separate lines
    if (order.clienteCompanyName && order.clienteContactName) {
      return (
        <div className="max-w-xs">
          <div className="flex items-center space-x-2">
            <BlacklistAlert client={clientForAlert} size="sm" />
            <div className={`text-sm font-bold leading-tight truncate ${
              isArchived ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'
            }`} title={order.clienteCompanyName}>
              {order.clienteCompanyName}
            </div>
          </div>
          <div className={`text-sm leading-tight truncate ${
            isArchived ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-300'
          }`} title={order.clienteContactName}>
            {order.clienteContactName}
          </div>
          {order.clientePhone && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {order.clientePhone}
            </div>
          )}
        </div>
      );
    }
    
    // For individual clients or fallback, use the existing display
    return (
      <div className="max-w-xs">
        <div className="flex items-center space-x-2">
          <BlacklistAlert client={clientForAlert} size="sm" />
          <div className={`text-sm leading-tight truncate ${
            isArchived ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'
          }`} title={getClientDisplayName(order)}>
            {getClientDisplayName(order)}
          </div>
        </div>
        {order.clientePhone && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {order.clientePhone}
          </div>
        )}
      </div>
    );
  };

  const getTechnicianDisplayNames = (order) => {
    if (order.technicianNames && order.technicianNames.length > 0) {
      return order.technicianNames.join(', ');
    }
    if (order.tecnicos && order.tecnicos.length > 0) {
      return `${order.tecnicos.length} técnico(s)`;
    }
    return 'Sin asignar';
  };

  const getRelativeTime = (date) => {
    if (!date) return '';
    
    let orderDate;
    if (date instanceof Date) {
      orderDate = date;
    } else if (date.toDate && typeof date.toDate === 'function') {
      // Firestore Timestamp
      orderDate = date.toDate();
    } else if (typeof date === 'string') {
      // String date from IndexedDB
      orderDate = new Date(date);
    } else if (date && typeof date === 'object' && date.seconds) {
      // Serialized Firestore Timestamp from IndexedDB
      orderDate = new Date(date.seconds * 1000);
    } else {
      // Fallback
      orderDate = new Date(date);
    }
    
    if (isNaN(orderDate.getTime())) {
      return '';
    }
    const now = new Date();
    const diffInMs = now - orderDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return '(hoy)';
    } else if (diffInDays === 1) {
      return '(ayer)';
    } else if (diffInDays < 7) {
      return `(hace ${diffInDays} días)`;
    } else if (diffInDays < 14) {
      return '(hace 1 semana)';
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `(hace ${weeks} semanas)`;
    } else if (diffInDays < 60) {
      return '(hace 1 mes)';
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `(hace ${months} meses)`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return years === 1 ? '(hace 1 año)' : `(hace ${years} años)`;
    }
  };

  const getEquipmentSummary = (order, isArchived = false) => {
    if (!order.equipos || order.equipos.length === 0) {
      return 'Sin equipos';
    }
    
    return order.equipos.map((equipo, index) => {
      if (equipo.type === 'exempt' || !equipo.catalogData) {
        // For exempt equipment: descripcion, series with S/N label
        return (
          <div key={index} className={`text-xs leading-tight break-words ${
            isArchived ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'
          }`}>
            <span>{equipo.descripcion}</span>
            {equipo.series && (
              <span> S/N: {equipo.series}</span>
            )}
          </div>
        );
      } else {
        // For catalog equipment: type, brand, model (bold), part number (parenthesis), S/N: serial
        const catalogData = equipo.catalogData;
        return (
          <div key={index} className={`text-xs leading-tight break-words ${
            isArchived ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'
          }`}>
            {catalogData?.tipo && <span>{catalogData.tipo} </span>}
            {catalogData?.marca && <BrandBadge brand={getBrandByNameOrId(catalogData.marca)} size="xs" className="mr-1" />}
            {catalogData?.modelo && <span className="font-bold">{catalogData.modelo}</span>}
            {catalogData?.numeroParte && <span> ({catalogData.numeroParte})</span>}
            {equipo.series && <span> S/N: {equipo.series}</span>}
          </div>
        );
      }
    });
  };

  // All possible order types for filters (not just those present in current orders)
  const orderTypes = ['Reparación', 'Ensamble', 'Garantía', 'Fabricación'];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver al Dashboard</span>
          </Link>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Órdenes de Trabajo</h2>
              </div>
              <div className="flex items-center space-x-2">
                {/* Cache Refresh Button */}
                {showCacheRefreshButtons && (
                  <button
                    onClick={handleForceRefresh}
                    disabled={isRefreshing}
                    className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Actualizar datos desde servidor"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                )}
                {canEdit() && (
                  <Link 
                    to="/orders/new"
                    className="flex items-center justify-center w-10 h-10 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    title="Nueva Orden"
                  >
                    <Plus className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Search Bar and Filters */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-4">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md mb-4">
                {success}
              </div>
            )}
            
            <div className="space-y-4">
              {/* Search Input */}
              <div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Buscar por folio, cliente, equipo, técnico..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Limpiar búsqueda"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <SearchCounter 
                  count={filteredOrders.length} 
                  label="órdenes" 
                  isSearching={!!debouncedSearchTerm || typeFilter !== 'all' || showBookmarked} 
                />
              </div>
              
              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-4">
                {/* Order Type Filter */}
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Tipo:</span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setTypeFilter('all')}
                      className={`px-3 py-1 text-xs rounded-full ${
                        typeFilter === 'all' 
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      Todos
                    </button>
                    {orderTypes.map(type => (
                      <button
                        key={type}
                        onClick={() => setTypeFilter(type)}
                        className={`px-3 py-1 text-xs rounded-full ${
                          typeFilter === type 
                            ? getOrderTypeColor(type)
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {getOrderTypeDisplayText(type)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Bookmark and Archive Toggles */}
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showBookmarked"
                    checked={showBookmarked}
                    onChange={(e) => setShowBookmarked(e.target.checked)}
                    className="h-4 w-4 text-yellow-600 dark:text-yellow-400 focus:ring-0 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                  <label htmlFor="showBookmarked" className="ml-2 flex items-center text-sm text-gray-900 dark:text-white">
                    <Bookmark className="h-4 w-4 mr-1" />
                    Solo favoritas
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showArchived"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-0 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                  <label htmlFor="showArchived" className="ml-2 block text-sm text-gray-900 dark:text-white">
                    Mostrar archivadas
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Orders List */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Cargando órdenes...
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {debouncedSearchTerm || typeFilter !== 'all' || showBookmarked
                  ? 'No se encontraron órdenes que coincidan con los filtros' 
                  : 'No hay órdenes registradas'}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        {/* Desktop headers */}
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                          Folio
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                          Fecha
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                          Tipo
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                          Cliente
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                          Equipos
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-48 hidden md:table-cell">
                          Estado
                        </th>
                        
                        {/* Mobile headers */}
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider md:hidden">
                          Orden
                        </th>
                        
                        {canEdit() && (
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Acciones
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {paginatedOrders.map((order) => (
                        <tr key={order.id} className={`cursor-pointer ${
                            order.isArchived 
                              ? 'bg-gray-100 dark:bg-gray-900 hover:bg-gray-200 dark:hover:bg-gray-800' 
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                            onClick={() => window.location.href = buildUrlWithState(`/orders/${order.id}`)}>
                          
                          {/* Desktop columns */}
                          <td className={`px-3 py-4 text-sm font-medium hidden md:table-cell ${
                            order.isArchived 
                              ? 'text-gray-600 dark:text-gray-400' 
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            <div className="flex items-center space-x-2">
                              <div className="font-semibold">
                                {order.folio}
                              </div>
                              {order.isBookmarked && (
                                <Bookmark className="h-4 w-4 text-yellow-600 dark:text-yellow-400 fill-current" title="Orden favorita" />
                              )}
                            </div>
                            {order.isArchived && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 mt-1">
                                Archivada
                              </span>
                            )}
                          </td>
                          <td className={`px-3 py-4 text-sm hidden md:table-cell ${
                            order.isArchived 
                              ? 'text-gray-600 dark:text-gray-400' 
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            <div>
                              {formatDate(order.fecha)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {getRelativeTime(order.fecha)}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-sm hidden md:table-cell">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                              {getOrderTypeDisplayText(order.tipo_de_orden)}
                            </span>
                          </td>
                          <td className={`px-3 py-4 text-sm hidden md:table-cell ${
                            order.isArchived 
                              ? 'text-gray-600 dark:text-gray-400' 
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            {renderClientInfo(order, order.isArchived)}
                          </td>
                          <td className={`px-3 py-4 text-sm hidden lg:table-cell ${
                            order.isArchived 
                              ? 'text-gray-600 dark:text-gray-400' 
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            <div className="max-w-xs">
                              <div className="space-y-1">
                                {getEquipmentSummary(order, order.isArchived)}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm w-48 hidden md:table-cell">
                            <div className="space-y-1">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(order.status_orden)}`}>
                                {order.status_orden || 'Sin estado'}
                              </span>
                              <div>
                                {order.estatus_pago ? (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(order.estatus_pago)}`}>
                                    {order.estatus_pago}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          {/* Mobile "Orden" column - contains all info */}
                          <td className="px-3 py-4 text-sm md:hidden">
                            <div className="space-y-2">
                              {/* Folio and archived status */}
                              <div>
                                <div className="flex items-center space-x-2">
                                  <div className={`font-semibold ${
                                    order.isArchived 
                                      ? 'text-gray-600 dark:text-gray-400' 
                                      : 'text-gray-900 dark:text-white'
                                  }`}>
                                    {order.folio}
                                  </div>
                                  {order.isBookmarked && (
                                    <Bookmark className="h-4 w-4 text-yellow-600 dark:text-yellow-400 fill-current" title="Orden favorita" />
                                  )}
                                </div>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                                    {getOrderTypeDisplayText(order.tipo_de_orden)}
                                  </span>
                                  {order.isArchived && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                      Archivada
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Date */}
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium text-gray-600 dark:text-gray-400">Fecha:</span> <span className="text-gray-500 dark:text-gray-400">{formatDate(order.fecha)} {getRelativeTime(order.fecha)}</span>
                              </div>
                              
                              {/* Client */}
                              <div className="flex items-center space-x-2 text-xs">
                                <span className="font-medium text-gray-600 dark:text-gray-400">Cliente:</span>
                                <BlacklistAlert client={{
                                  isBlacklisted: clientBlacklistStatus[order.cliente]?.isBlacklisted || false,
                                  activeIncidentsCount: clientBlacklistStatus[order.cliente]?.activeIncidentsCount || 0,
                                  mostRecentIncidentMotivo: clientBlacklistStatus[order.cliente]?.mostRecentIncidentMotivo || null
                                }} size="sm" />
                                <span className={order.isArchived 
                                  ? 'text-gray-600 dark:text-gray-400' 
                                  : 'text-gray-900 dark:text-white'
                                }>{getClientDisplayName(order)}</span>
                              </div>
                              
                              {/* Equipment */}
                              <div className="text-xs">
                                <span className="font-medium text-gray-600 dark:text-gray-400">Equipos:</span>
                                <div className={`mt-1 ${
                                  order.isArchived 
                                    ? 'text-gray-600 dark:text-gray-400' 
                                    : 'text-gray-900 dark:text-white'
                                }`}>
                                  {getEquipmentSummary(order, order.isArchived)}
                                </div>
                              </div>
                              
                              {/* Status */}
                              <div className="flex flex-wrap gap-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(order.status_orden)}`}>
                                  {order.status_orden || 'Sin estado'}
                                </span>
                                {order.estatus_pago ? (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(order.estatus_pago)}`}>
                                    {order.estatus_pago}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500 text-xs">Sin estatus de pago</span>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          {/* Actions column */}
                          {canEdit() && (
                            <td className="px-2 py-4 text-sm font-medium">
                              <div className="flex flex-col space-y-1" onClick={(e) => e.stopPropagation()}>
                                <Link
                                  to={buildUrlWithState(`/orders/${order.id}/edit`)}
                                  className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded flex justify-center"
                                  title="Editar orden"
                                >
                                  <Edit className="h-4 w-4" />
                                </Link>
                                {order.isArchived ? (
                                  <button
                                    onClick={() => handleUnarchive(order)}
                                    className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 rounded flex justify-center"
                                    title="Desarchivar orden"
                                  >
                                    <ArchiveRestore className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleArchive(order)}
                                    className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded flex justify-center"
                                    title="Archivar orden"
                                  >
                                    <Archive className="h-4 w-4" />
                                  </button>
                                )}
                                {canDelete() && (
                                  <button
                                    onClick={() => handleDelete(order)}
                                    className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded flex justify-center"
                                    title="Eliminar orden"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 mt-6">
                    {/* Mobile pagination */}
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>

                    {/* Desktop pagination */}
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                          <span className="font-medium">{Math.min(startIndex + itemsPerPage, filteredOrders.length)}</span> de{' '}
                          <span className="font-medium">{filteredOrders.length}</span> resultados
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Anterior</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                            </svg>
                          </button>

                          {(() => {
                            const pageNumbers = [];
                            const showEllipsis = totalPages > 7;
                            
                            if (showEllipsis) {
                              if (currentPage <= 3) {
                                for (let i = 1; i <= Math.min(5, totalPages); i++) {
                                  pageNumbers.push(i);
                                }
                                if (totalPages > 5) {
                                  pageNumbers.push('...');
                                  pageNumbers.push(totalPages);
                                }
                              } else if (currentPage >= totalPages - 2) {
                                pageNumbers.push(1);
                                pageNumbers.push('...');
                                for (let i = Math.max(totalPages - 4, 1); i <= totalPages; i++) {
                                  if (i > 1) pageNumbers.push(i);
                                }
                              } else {
                                pageNumbers.push(1);
                                pageNumbers.push('...');
                                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                                  pageNumbers.push(i);
                                }
                                pageNumbers.push('...');
                                pageNumbers.push(totalPages);
                              }
                            } else {
                              for (let i = 1; i <= totalPages; i++) {
                                pageNumbers.push(i);
                              }
                            }
                            
                            return pageNumbers.map((number, index) => (
                              number === '...' ? (
                                <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-offset-0">
                                  ...
                                </span>
                              ) : (
                                <button
                                  key={number}
                                  onClick={() => handlePageChange(number)}
                                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 ${
                                    currentPage === number
                                      ? 'z-10 bg-blue-600 dark:bg-blue-700 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-400'
                                      : 'text-gray-900 dark:text-gray-300'
                                  }`}
                                >
                                  {number}
                                </button>
                              )
                            ));
                          })()}

                          <button
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Siguiente</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersList;